<template>
  <div>
    <backButton />

        <v-layout row>
         <v-flex xs12>
          <template v-if="page">
              <!-- Add page title -->
              <v-layout row>
                <v-flex sm9>
                  <h2 class="mb-3">{{ page.Name[currentLanguage] }}</h2>
                </v-flex>
                <v-flex sm3 class="text-right">
                  <template v-if="userIsAdministrator && !page.Custom">
                    <v-switch class="float-right mt-0" v-model="editMode" light v-bind:label="$t('Edit mode')"></v-switch>
                  </template>
                </v-flex>
              </v-layout>

              <!-- The page has only one item -->
              <template v-if="!page.RepeatingContent && page.Custom">
                 <component :is="customComponent"></component>
              </template>
              <template v-else-if="!page.RepeatingContent && content && Object.keys(content).length > 0">
                <v-form ref="form" id="editPageForm" lazy-validation>
                  <template class="mb-3" v-for="(item,index) in content[0].Data">
                      <pageElement
                      :key=index
                      :item="item"
                      :editMode="userIsAdministrator && editMode"
                      :websiteId="page.WebsiteId.$oid"
                      :pageId="page._id.$oid"
                      :objectName="index"
                      @removeItem="removeItem(index)"
                      ref="pageElement"
                      />
                  </template>
                </v-form>

                <template v-if="userIsAdministrator && editMode">
                  <br>
                  <v-card class="mb-4">
                    <v-card-text>
                      <h3 class="mb-3">{{ $t('Add item') }}</h3>
                      <addRemovePageElement
                        @addItem="addObjectToPageContent"
                        :enableAdd="true"
                      />
                    </v-card-text>
                  </v-card>
                </template>
              </template>
              <!-- The page has repeating objects -->
              <template v-else-if="page.RepeatingContent">
                <!-- The page has no content definition -->
                <v-alert v-if="Object.keys(page.ContentDefinition).length == 0 && !editMode" type="info" :value="true" class="mb-3">
                  {{ $t('This page has not been configured yet') }}
                </v-alert>
                <!-- List repeating page items -->
                <template v-else-if="!editMode">
                    <addButton :toRoute="{ name: 'editPageAddItem'}" :label="$t('Add item')"/>
                    <template v-if="userIsAdministrator">
                      <v-btn v-on:click="exportPageContent()" class="mb-4 ml-0 add-button" color="info" dark>
                         {{ $t('Export items') }}
                         <v-icon right>call_made</v-icon>
                      </v-btn>
                    </template>

                    <v-card>
                        <v-layout custom-overview-row row v-for="(item, index) in content" :key="item.DisplayLabel">
                         <v-flex lg2 sm3 xs3 pt-4 pl-3 pb-3 subheading>
                           <v-btn class="edit-page-action-button sort-button" text small icon color="grey" v-on:click="moveUp(index)">
                             <v-icon>keyboard_arrow_up</v-icon>
                           </v-btn>

                           <v-btn class="edit-page-action-button sort-button" text small icon color="grey" v-on:click="moveDown(index)">
                             <v-icon>keyboard_arrow_down</v-icon>
                           </v-btn>
                         </v-flex>
                         <v-flex custom-overview-row-actions lg8 sm6 xs4 text-xs-right>
                           <v-list-item-title v-html="item.DisplayLabel"></v-list-item-title>
                         </v-flex>
                         <v-flex custom-overview-row-actions lg2 sm3 xs4 pt-3 pl-3 pb-3 subheading>
                           <router-link :to="{ name: 'editPageAddEditItem', params: { id: page._id.$oid, itemId: item._id.$oid }}">
                             <v-btn icon color="orange">
                               <v-icon>edit</v-icon>
                             </v-btn>
                           </router-link>

                           <router-link :to="{ name: 'deletePageItem', params: { id: page._id.$oid, itemId: item._id.$oid }}">
                             <v-btn icon color="red">
                               <v-icon>delete</v-icon>
                             </v-btn>
                           </router-link>
                         </v-flex>
                        </v-layout>
                    </v-card>
                    <infinite-loading @infinite="getMinifiedContent">
                      <div slot="spinner"><preloader></preloader></div>
                      <div slot="no-more"></div>
                      <div slot="no-results"></div>
                    </infinite-loading>
                  </template>

                <!-- Edit repeating objects content definition -->
                <template v-if="userIsAdministrator && editMode">
                  <v-card class="mb-4">
                    <v-card-text>
                      <h3 class="mb-3">{{ $t('Edit content definition') }}</h3>
                    </v-card-text>

                    <v-form ref="contentDefinitionform" lazy-validation>
                      <template v-for="(item, index) in page.ContentDefinition">
                        <v-layout :key=index class="custom-overview-row mt-4" v-bind:class="{ 'no-border': item.Type == 'dropdown' }" row wrap>
                          <v-flex xs9 sm10>
                            <v-layout class="custom-overview-row" v-bind:class="{ 'no-border': item.Type == 'dropdown' }"  row wrap>
                              <v-flex custom-overview-row-title xs12 sm4>
                                <h4 class="mt-4">{{ index }}</h4>
                              </v-flex>

                              <v-flex custom-overview-row-title xs12 sm4>
                                  <v-text-field
                                  :label="$t('Object name')"
                                  v-model="item.Title"
                                  required
                                  :rules="[(v) => !!v || $t('Name is required'),(v) => v.length >= 2 || $t('Minimum required characters is 2')]"
                                  validate-on-blur
                                  class="ml-4"
                                  ></v-text-field>

                                  <template v-if="item.Type == 'content'">
                                    <v-select
                                      v-bind:items="getSharedPagesNotAssigned()"
                                      single-line
                                      v-model="item.LinkedPage"
                                      item-value ="_id"
                                      :label="$t('Select a page')"
                                      class="altSelect ml-4"
                                    >
                                      <template slot="selection" slot-scope="data">
                                        <div class="input-group__selections__comma">{{ data.item.Name[currentLanguage] }}</div>
                                      </template>
                                      <template slot="item" slot-scope="data">
                                        {{ data.item.Name[currentLanguage] }}
                                      </template>
                                    </v-select>
                                  </template>
                                  <template v-else-if="item.Type == 'croppedimage'">
                                    <v-text-field
                                    :label="$t('Width in pixels')"
                                    v-model="item.Width"
                                    class="float-left mr-3 ml-4"
                                    ></v-text-field>

                                    <v-text-field
                                    :label="$t('Height in pixels')"
                                    v-model="item.Height"
                                    ></v-text-field>
                                  </template>
                              </v-flex>

                              <v-flex custom-overview-row-title xs12 sm2>
                                <h4 class="mt-4 mb-4 item-type">{{ item.Type }}</h4>
                              </v-flex>

                              <v-flex custom-overview-row-title xs12 sm2>
                                <v-switch
                                  v-if="item.Type == 'text' || item.Type == 'richtext' || item.Type == 'image' || item.Type == 'croppedimage' || item.Type == 'gallery'"
                                  v-model="item.Translate"
                                  class="mt-3 ml-2 mb-0"
                                  light
                                  v-bind:label="$t('Translate item')"
                                ></v-switch>

                                <v-switch
                                  v-if="item.Type == 'text' || item.Type == 'richtext' || item.Type == 'datepicker' || item.Type == 'dropdown' || item.Type == 'content'"
                                  v-model="item.Required"
                                  class="mt-0 ml-2"
                                  :label="$t('Field is required')">
                                </v-switch>

                                <v-switch
                                  v-if="item.Type == 'image' || item.Type == 'croppedimage' || item.Type == 'gallery'"
                                  v-model="item.AddAltTags"
                                  class="mt-0 ml-2"
                                  :label="$t('Add alt tags')">
                                </v-switch>

                                <v-switch
                                  v-if="item.Type == 'content'"
                                  class="mt-0 ml-2"
                                  v-model="item.LoadItemsWhenCreated"
                                  :label="$t('Load items when created')"
                                ></v-switch>
                              </v-flex>
                            </v-layout>
                          </v-flex>

                          <v-flex custom-overview-row-actions xs3 sm2>
                            <v-btn v-on:click="removeContentDefinitionItem(index)" icon color="red">
                              <v-icon>delete</v-icon>
                            </v-btn>
                          </v-flex>
                        </v-layout>

                        <template v-if="item.Type == 'dropdown'">
                          <v-layout :key=index class="custom-overview-row" row wrap>
                            <v-card-text>
                              <v-layout row wrap>
                                <v-flex xs12 sm10>
                                  <v-layout row wrap>
                                    <v-flex sm4 xs12>
                                      <b><i>{{ $t('Configure dropdown options') }}:</i></b>
                                    </v-flex>

                                    <v-flex sm6 xs12>
                                      <template v-for="(option, optionIndex) in item.Options">
                                        <v-chip :key=optionIndex close @input="removeOption(index, optionIndex)">
                                          {{ option.Value }}
                                        </v-chip>
                                      </template>

                                      <v-layout row wrap>
                                          <v-flex sm6>
                                            <v-text-field
                                            :label="$t('Option name')"
                                            v-model="newOptionName"
                                            :error="addOptionFieldHasError"
                                            :rules="addOptionFieldRule"
                                            class="no-styling ml-1"
                                            ></v-text-field>
                                          </v-flex>
                                          <v-flex sm6>
                                            <v-btn class="mt-3 edit-page-action-button dropdown-action-button" color="green" dark v-on:click="addOption(index)">
                                             {{ $t('Add option') }}
                                             <v-icon>add</v-icon>
                                            </v-btn>
                                          </v-flex>
                                      </v-layout>
                                    </v-flex>
                                  </v-layout>
                                </v-flex>

                                <v-flex xs12 sm2>
                                </v-flex>
                              </v-layout>
                            </v-card-text>
                          </v-layout>
                        </template>
                      </template>
                    </v-form>
                  </v-card>

                  <v-card class="mb-4">
                    <v-card-text>
                      <h3 class="mb-3">{{ $t('Add element to content definition') }}</h3>

                      <addRemovePageElement
                        @addItem="addObjectToContentDefinition"
                        :enableAdd="true"
                      />
                    </v-card-text>
                  </v-card>
                </template>
              </template>

              <template v-if="page.RepeatingContent && editMode">
                <cancelConfirmButtons :confirmFunction="saveContentDefinition" :loading="savingContentDefinition" :confirmLabel="$t('Save page definition')" />
              </template>
              <template v-else-if="!page.RepeatingContent && !this.page.Custom">
                <cancelConfirmButtons :confirmFunction="saveContent" :loading="savingContent" :confirmLabel="$t('Save page')" />
              </template>
          </template>
          <template v-else-if="error.length == 0">
            <preloader></preloader>
          </template>

          <v-alert type="success" v-if="success.length > 0" class="mt-2" icon="check" :value="true">
            {{ success }}
          </v-alert>
          <v-alert v-if="error.length > 0" class="mt-2" color="error" icon="warning" :value="true">
            {{ error }}
          </v-alert>
         </v-flex>
        </v-layout>
  </div>
</template>

<script>
import request from '../../request'
import helpers from '../../helpers'
import config from '../../config'
import auth from '../../auth'
import Vue from 'vue'

import { mapGetters } from 'vuex'

import InfiniteLoading from 'vue-infinite-loading';

export default {
  data () {
    return {
      error: "",
      success:"",
      userToken: localStorage.user == null ? "" : JSON.parse(localStorage.user).Token,
      page: null,
      content: null,
      userIsAdministrator: auth.userIsAdministrator(),
      editMode:false,
      froalaOptions: {
       toolbar : ["bold", "italic", "underline", "|", "align", "formatOL", "formatUL"],
       toolbarMD : ["bold", "italic", "underline", "|", "align", "formatOL", "formatUL"]
      },
      file:null,
      objectTypes: config.objectTypes,
      newOptionName:"",
      savingContent:false,
      savingContentDefinition:false,
      addOptionFieldHasError:false,
      addOptionFieldRule:[]
    }
  },
  created () {
    this.getPage()
  },
  watch: {
    '$route.params.id': function () {
      this.getPage()
    }
  },
  computed: {...mapGetters({
      selectedWebsite: 'selectedWebsite'
    }),
    currentLanguage:{
      get: function () {
        return Vue.i18n.locale()
      }
    },
    customComponent () {
     return () => this.page && this.page.Custom ? import('@/components/custom/' + this.page.UniqueName + '.vue') : null
    }
  },
  components: {
    InfiniteLoading,
  },
  methods: {
    saveContentDefinition(){
      if (this.$refs.contentDefinitionform.validate()) {
        //used when saving the content definition
        this.savingContentDefinition = true

        request.put("/page", this.page, (res) => {
          this.savingContentDefinition = false

          if (res.success) {
            this.editMode = false
          }else{
            this.error = res.message
          }
        })
      }
    },
    saveContent(){
      //used when saving a page with non-repeating objects
      this.error = ""

      var formCustomElementsValid = helpers.validateFormPageElements("editPageForm")
      var formIsValid = this.$refs.form.validate()

      if (formCustomElementsValid && formIsValid) {
        this.savingContent = true
        this.content[0].DisplayLabel = helpers.getDisplayLabel(this.content[0].Data)

        request.put("/content", this.content[0], (res) => {
          this.savingContent = false

          if (res.success) {
            this.success = Vue.i18n.translate('The page was successfully saved')
            setTimeout(()=>{
              this.success = ""
            },config.hideMessageTimeout)
          }else{
            this.error = res.message
          }
        })
      }else{
        this.error = Vue.i18n.translate('Please make sure all text fields have a value')
      }
    },
    getPage(){
        this.page == null

        request.get("/page/"+ this.$route.params.id, null, (res) => {
          if (res.success) {
            this.page = res.data;
            this.$store.dispatch('setSelectedWebsiteById', this.page.WebsiteId)

            if(!this.page.Custom){
              if(!this.page.RepeatingContent){
                this.getPageContent()
              }else{
                if(this.page.ContentDefinition == null){
                  this.page.ContentDefinition = {}
                }
              }
            }
          }else{
            this.error = res.message
          }
        })
    },
    getPageContent(){
      this.content == null

      //Content = array of content objects
      //This function is preferably used on pages with non-repeating objects
      request.get("/pagecontent/"+ this.$route.params.id, null, (res) => {
        if (res.success) {
          this.content = res.data

          //Non-repeating pages have only one content object
          if(this.content[0].Data == null){
            this.content[0].Data = {}
          }

          if(this.content[0].DisplayLabel == null){
            this.content[0].DisplayLabel = ""
          }
        }else{
          this.error = res.message
        }
      })
    },
    getMinifiedContent($state){
      //Content = array of minified content objects.
      //This function is preferably used on pages with repeating objects
      request.get("/minifiedpagecontent/"+ this.$route.params.id + "?start=" + (this.content == null ? 0 : this.content.length), null, (res) => {
        if (res.success) {
          if(this.content == null){
            this.content = res.data
          }else{
            this.content = this.content.concat(res.data)
          }

          $state.loaded();

          if(this.content.length == this.page.ContentCount){
            $state.complete();
          }
        }else{
          this.error = res.message
        }
      })
    },
    removeItem(index){
        Vue.delete(this.content[0].Data,index)
    },
    addObjectToPageContent(properties){
      var newObject = {
          "Title" : properties.objectTitle,
          "Value" : helpers.createEmptyValueBasedOnType(properties.objectType),
          "Type" : properties.objectType,
          "Translate": properties.objectLanguages,
          "Options": [],
          "Properties": []
      };

      if(properties.objectType == "text"
        || properties.objectType == "richtext"
        || properties.objectType == "datepicker"
        || properties.objectType == "dropdown"
        || properties.objectType == "content"){
        newObject["Required"] = "false";
      }

      if(properties.objectType == "image"
        || properties.objectType == "croppedimage"
        || properties.objectType == "gallery"){
        newObject["AddAltTags"] = "true";
      }

      Vue.set(this.content[0].Data,properties.objectName, newObject)
    },
    addObjectToContentDefinition(properties){
      var newObject = {
          "Title" : properties.objectTitle,
          "Value" : helpers.createEmptyValueBasedOnType(properties.objectType),
          "Type" : properties.objectType,
          "Translate": properties.objectLanguages,
          "Options": [],
          "Properties": []
      };

      if(properties.objectType == "text"
        || properties.objectType == "richtext"
        || properties.objectType == "datepicker"
        || properties.objectType == "dropdown"
        || properties.objectType == "content"){
        newObject["Required"] = "false";
      }

      if(properties.objectType == "image"
        || properties.objectType == "croppedimage"
        || properties.objectType == "gallery"){
        newObject["AddAltTags"] = "true";
      }

      Vue.set(this.page.ContentDefinition,properties.objectName, newObject)
    },
    removeContentDefinitionItem(index){
      Vue.delete(this.page.ContentDefinition,index);
    },
    removeOption(index,optionIndex){
      this.page.ContentDefinition[index].Options.splice(optionIndex,1)
    },
    addOption(index){
      this.addOptionFieldHasError = false
      this.addOptionFieldRule = []

      if(this.newOptionName.length > 1){
        this.page.ContentDefinition[index].Options.push({Id:this.page.ContentDefinition[index].Options.length+1,Value:this.newOptionName})
      }else{
        this.addOptionFieldRule = [() => Vue.i18n.translate('Minimum required characters is') + ' 2']
        this.addOptionFieldHasError = true
      }
    },
    moveUp(index){
      var newSort
      var oldSort = this.content[index].Sort

      if(index == 0){
        newSort = this.content[this.content.length-1].Sort
      }else{
        newSort = this.content[index-1].Sort
      }

      if(newSort == oldSort){
        newSort +=1
      }

      var indexToSwapWith = index-1

      if(index == 0){
        indexToSwapWith = this.content.length-1
      }

      this.content[index].Sort = newSort
      this.content[indexToSwapWith].Sort = oldSort

      this.updateContentSort([this.content[index],this.content[indexToSwapWith]])
    },
    moveDown(index){
      var newSort
      var oldSort = this.content[index].Sort

      if(index == this.content.length-1){
        newSort = this.content[0].Sort
      }else{
        newSort = this.content[index+1].Sort
      }

      if(newSort == oldSort){
        newSort -=1
      }

      var indexToSwapWith = index + 1

      if(indexToSwapWith == this.content.length){
        indexToSwapWith = 0
      }

      this.content[index].Sort = newSort
      this.content[indexToSwapWith].Sort = oldSort

      this.updateContentSort([this.content[index],this.content[indexToSwapWith]])
    },
    updateContentSort(items){
      request.put("/content/updatesort", items, (res) => {
        if (res.success) {
          this.content = this.content.sort(function(a, b){ return a.Sort - b.Sort; });
        }else{
          this.error = res.message
        }
      })
    },
    exportPageContent(){
      request.download("/exportcontent/"+ this.page._id.$oid, { responseType: 'blob' }, "export_ " + this.page._id.$oid + ".xlsx")
    },
    getSharedPagesNotAssigned(){
        return this.selectedWebsite.Pages.filter(p => p.RepeatingContent === true)
    },
  }
}
</script>

<style>
  hr{
    border-top:1px solid #bbb;
  }

  #editPageForm .textInput.error,
  #editPageForm .editr--content.error{
    background-color:transparent !important;
  }

  #editPageForm .editr--content.error{
    border-width: 1px;
    border-style: solid;
  }

  .sort-button{
    margin-top:5px;
  }

  @media only screen and (max-width: 600px) {
    .item-type{
      margin-top:0 !important;
    }
  }

  @media only screen and (max-width: 495px) {
    .sort-button{
      margin-top:-3px;
    }
  }
</style>
